/**
 * The course-section form's state shape and its two wire payloads.
 *
 * Split from `components/courses/CourseSectionFields.tsx` for the same reason
 * `term-state.ts` is split from the pill that renders it: a module exporting
 * both components and plain functions breaks Fast Refresh, and these are
 * imported by pages that render none of the controls.
 */
import type { Program, Semester } from "@/lib/api/academicStructure";
import type {
  Course,
  CreateCourse,
  Modality,
  PatchCourse,
} from "@/lib/api/courses";
import type { MeetingProvider } from "@/lib/api/tenant";
import { DEFAULT_COURSE_TIMEZONE } from "@/lib/course-schedule";

export const NONE = "__none__";

export const WEEKDAY_OPTIONS = [
  [0, "Sun"], [1, "Mon"], [2, "Tue"], [3, "Wed"], [4, "Thu"], [5, "Fri"], [6, "Sat"],
] as const;

export const TIME_OPTIONS = Array.from({ length: 96 }, (_, index) => {
  const hour = Math.floor(index / 4);
  const minute = (index % 4) * 15;
  const value = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  const displayHour = hour % 12 || 12;
  return { value, label: `${displayHour}:${String(minute).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}` };
});

/**
 * Every field a section owns, in one object rather than fourteen `useState`
 * pairs, so create and edit cannot drift in what they cover.
 * `emptyCourseForm`/`courseFormFrom` are the only two constructors.
 */
export interface CourseForm {
  code: string;
  title: string;
  credits: string;
  modality: Modality;
  programId: string;
  semesterId: string;
  instructorFacultyProfileId: string;
  meetingProvider: MeetingProvider;
  meetingWeekdays: number[];
  meetingStartsAt: string;
  meetingEndsAt: string;
  timezone: string;
  meetingUrl: string;
  location: string;
  capacity: string;
  imageUrl: string;
}

export function emptyCourseForm(meetingProvider: MeetingProvider): CourseForm {
  return {
    code: "", title: "", credits: "", modality: "online",
    programId: NONE, semesterId: NONE, instructorFacultyProfileId: NONE,
    meetingProvider, meetingWeekdays: [], meetingStartsAt: "", meetingEndsAt: "",
    // A section cannot be saved without a zone (migration 061), so the form
    // opens on the institutional one rather than on a blank the server rejects.
    timezone: DEFAULT_COURSE_TIMEZONE,
    meetingUrl: "", location: "", capacity: "", imageUrl: "",
  };
}

export function courseFormFrom(course: Course): CourseForm {
  return {
    code: course.code,
    title: course.title,
    credits: course.credits != null ? String(course.credits) : "",
    modality: course.modality,
    programId: course.program_id ?? NONE,
    semesterId: course.semester_id ?? NONE,
    instructorFacultyProfileId: course.primary_instructor_faculty_profile_id ?? NONE,
    meetingProvider: course.meeting_provider,
    meetingWeekdays: course.meeting_weekdays ?? [],
    meetingStartsAt: (course.meeting_starts_at ?? "").replace(/:00(?:\.0+)?$/, (match, offset) => offset === 5 ? "" : match),
    meetingEndsAt: (course.meeting_ends_at ?? "").replace(/:00(?:\.0+)?$/, (match, offset) => offset === 5 ? "" : match),
    timezone: course.timezone ?? DEFAULT_COURSE_TIMEZONE,
    meetingUrl: course.meeting_url ?? "",
    location: course.location ?? "",
    capacity: course.capacity != null ? String(course.capacity) : "",
    imageUrl: course.image_url ?? "",
  };
}

/** The wire payload, in two halves, split exactly where write authority is.
 *
 *  `NONE`/empty become explicit nulls, never omitted keys — clearing a field has
 *  to reach the server as `null`.
 *
 *  The registrar half — `code`, `title`, `credits`, `program_id`, `semester_id`,
 *  `capacity`, `primary_instructor_faculty_profile_id` — is admin-only to write,
 *  and is therefore omitted WHOLESALE from a non-admin save rather than sent and
 *  refused. `courses.py` 403s each of those fields by name for a non-admin
 *  caller, so an omitted key and a refused key must not disagree.
 *
 *  Creation is admin-only, which is why `courseFormToPayload` can return
 *  `CreateCourse` with its required `code` and `title` rather than a partial
 *  shape with holes in it. */
export function teachingOperationalPayload(form: CourseForm): PatchCourse {
  const trimmedOrNull = (v: string) => v.trim() || null;
  return {
    modality: form.modality,
    meeting_provider: form.meetingProvider,
    meeting_weekdays: form.meetingWeekdays.length ? form.meetingWeekdays : null,
    meeting_starts_at: trimmedOrNull(form.meetingStartsAt),
    meeting_ends_at: trimmedOrNull(form.meetingEndsAt),
    // Never null: the column is NOT NULL and the API refuses a blank, so a
    // cleared select would be a 422 the administrator cannot act on.
    timezone: form.timezone.trim() || DEFAULT_COURSE_TIMEZONE,
    meeting_url: trimmedOrNull(form.meetingUrl),
    location: trimmedOrNull(form.location),
    image_url: trimmedOrNull(form.imageUrl),
  };
}

/**
 * Why an image link would be refused, or null when it is empty or acceptable.
 *
 * Mirrors the server's `https_image_url` so the editor can say so inline
 * instead of after a round trip. The server stays the authority: this only
 * decides whether Save is offered.
 */
export function courseImageUrlProblem(value: string): string | null {
  const candidate = value.trim();
  if (!candidate) return null;
  if (!/^https:\/\/[^/]/i.test(candidate)) return "Use a link that starts with https://.";
  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return "This link is incomplete.";
  }
  if (!url.hostname) return "This link is incomplete.";
  if (url.username || url.password) return "Use a link without a username or password.";
  return null;
}

export function courseFormToPayload(form: CourseForm): CreateCourse {
  return {
    ...teachingOperationalPayload(form),
    code: form.code.trim(),
    title: form.title.trim(),
    credits: form.credits === "" ? null : Number(form.credits),
    program_id: form.programId === NONE ? null : form.programId,
    semester_id: form.semesterId === NONE ? null : form.semesterId,
    primary_instructor_faculty_profile_id: form.instructorFacultyProfileId === NONE ? null : form.instructorFacultyProfileId,
    capacity: form.capacity === "" ? null : Number(form.capacity),
  };
}

/** What an edit actually sends, narrowed by who is saving it. */
export function courseEditPayload(form: CourseForm, canWriteRegistrarFields: boolean): PatchCourse {
  return canWriteRegistrarFields
    ? courseFormToPayload(form)
    : teachingOperationalPayload(form);
}

export interface InstructorOption {
  faculty_profile_id: string;
  label: string;
  eligible: boolean;
  archived?: boolean;
  /** Shown beside the name: why it cannot be chosen, or that it can but has no sign-in yet. */
  qualifier: "No sign-in yet" | "Deactivated" | "Unavailable" | "Archived" | null;
}

export interface CourseFormSources {
  /**
   * Whether the reference reads have landed.
   *
   * Every field below has a benign-looking empty fallback, and that is the
   * trap: an empty `programs` and a `defaultMeetingProvider` of `in_person`
   * read exactly like a real institution that owns no program and meets in
   * person. Anything that SNAPSHOTS one of these values — seeding a form,
   * deriving a route from a count — has to wait for this to be false, or it
   * commits to a placeholder and calls it the registrar's choice.
   */
  pending: boolean;
  unavailable?: string[];
  retry?: () => void;
  programs: Program[];
  semesters: Semester[];
  instructors: InstructorOption[];
  canWriteRegistrarFields: boolean;
  enabledProviders: MeetingProvider[];
  defaultMeetingProvider: MeetingProvider;
  locations: string[];
}

export type SetCourseField = <K extends keyof CourseForm>(key: K, value: CourseForm[K]) => void;
